<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Toast Notification -->
    <Toast :show="showToast" :message="toastMessage" :type="toastType" />

    <div class="flex-1 flex flex-col gap-5 container-padding pb-24 py-4">
      <!-- Section header -->
      <h3 class="text-sm font-semibold text-[var(--color-text)] opacity-70">
        {{ $t('views.more.focalLength.current') }}
      </h3>

      <!-- Current value display -->
      <div
        class="bg-[var(--color-primary-200)] p-6 rounded-2xl shadow-md border border-[var(--color-primary-400)]">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <div class="text-3xl font-bold font-mono text-[var(--color-text)]">
              {{ displayValue }}
            </div>
          </div>
          <button
            @click="showEditDialog = true"
            class="bg-[var(--color-primary-100)] hover:bg-[var(--color-primary-300)] p-3 rounded-xl transition border border-[var(--color-primary-400)]">
            <PencilIcon class="w-7 h-7 text-[var(--color-text)]" />
          </button>
        </div>
      </div>

      <!-- Eartag Preview Example -->
      <div v-if="hasPrefix">
        <h3 class="text-sm font-semibold mb-3 text-[var(--color-text)] opacity-70">
          {{ $t('views.more.eartagPrefix.preview.title') }}
        </h3>
        <div class="bg-[var(--color-primary-200)] p-6 rounded-2xl shadow-md border border-[var(--color-primary-400)]">
          <div class="text-xs uppercase opacity-60 mb-2 text-[var(--color-text)]">
            {{ $t('views.more.eartagPrefix.preview.example') }}
          </div>
          <div class="font-mono text-2xl font-bold text-[var(--color-text)] tracking-wider">
            {{ exampleEartag }}
          </div>
          <p class="text-xs mt-3 text-[var(--color-text)] opacity-60">
            {{ $t('views.more.eartagPrefix.preview.description') }}
          </p>
        </div>
      </div>

    </div>

    <!-- Edit Dialog -->
    <InputPopupModule
      v-model:show="showEditDialog"
      :title="$t('views.more.eartagPrefix.modal.title')"
      :subtitle="$t('views.more.eartagPrefix.modal.instructions')"
      :initial-data="{ prefix: currentPrefix }"
      :validation-fn="validatePrefix"
      @save="savePrefix">
      <template #content="{ formData, updateField }">
        <div>
          <label class="block text-sm font-semibold mb-2 text-[var(--color-text)]">
            {{ $t('views.more.eartagPrefix.modal.label') }}
          </label>
          <input
            :value="formData.prefix"
            @input="handleInput($event, formData, updateField)"
            type="text"
            :placeholder="$t('views.more.eartagPrefix.modal.placeholder')"
            maxlength="11"
            class="input-field font-mono text-lg tracking-wider"
            autofocus
          />

          <!-- Error Message -->
          <div
            v-if="errorMessage"
            class="mt-2 text-sm text-red-500 flex items-start gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ errorMessage }}</span>
          </div>
        </div>
      </template>
    </InputPopupModule>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Pencil as PencilIcon } from 'lucide-vue-next'
import { useUiStore } from '@/stores/ui.store.js'
import InputPopupModule from '@/components/ui/inputs/InputPopupModule.vue'
import Toast from '@/components/ui/feedback/Toast.vue'

const { t } = useI18n()
const uiStore = useUiStore()

onMounted(() => {
  uiStore.setHeader({
    title: t('views.more.eartagPrefix.title'),
    showBack: true,
    showAdd: false
  })
})

const showEditDialog = ref(false)
const errorMessage = ref('')

// Toast notification state
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('saved')

// Get current prefix
const hasPrefix = computed(() => uiStore.hasEartagPrefix())
const currentPrefix = computed(() => uiStore.getEartagPrefix() || '')
const displayValue = computed(() => {
  return hasPrefix.value ? currentPrefix.value : t('views.more.eartagPrefix.notSet')
})

/**
 * Generate example eartag by completing the prefix with dummy digits
 */
const exampleEartag = computed(() => {
  if (!hasPrefix.value) return 'DE 1234 567 89012'
  
  const prefix = currentPrefix.value
  const prefixClean = prefix.replace(/\s/g, '')
  
  // Need 12 digits total after DE
  const digitsInPrefix = prefixClean.length - 2 // Subtract "DE"
  const remainingDigits = 12 - digitsInPrefix
  
  if (remainingDigits <= 0) return prefix
  
  // Generate dummy digits to complete the eartag (use repeating pattern to ensure enough digits)
  const dummyPattern = 'XXXXXXXXXXXX' // 12 digits to cover all cases
  const dummyDigits = dummyPattern.substring(0, remainingDigits)
  const fullNumber = prefixClean.substring(2) + dummyDigits
  
  // Format as DE XXXX XXX XXXXX
  let formatted = 'DE '
  formatted += fullNumber.substring(0, 4)
  if (fullNumber.length > 4) {
    formatted += ' ' + fullNumber.substring(4, 7)
  }
  if (fullNumber.length > 7) {
    formatted += ' ' + fullNumber.substring(7, 12)
  }
  
  return formatted
})

/**
 * Validate prefix format - must be DE followed by 1-7 digits
 */
function isValidFormat(prefix) {
  if (!prefix) return false
  // Remove spaces and check: must be DE followed by 1-7 digits
  const cleaned = prefix.replace(/\s/g, '')
  const pattern = /^DE\d{1,7}$/
  return pattern.test(cleaned)
}

/**
 * Validation function for InputPopupModule
 */
function validatePrefix(formData) {
  const cleaned = formData.prefix?.trim() || ''
  return isValidFormat(cleaned)
}

/**
 * Format prefix as DE XXXX XXX (max 7 digits after DE)
 */
function formatPrefix(input) {
  // Remove all spaces and convert to uppercase
  const cleaned = input.replace(/\s/g, '').toUpperCase()
  
  // Limit to DE + max 7 digits
  const limited = cleaned.substring(0, 9) // DE + 7 digits
  
  // Build formatted string
  let formatted = ''
  
  // Add "DE" prefix (first 2 chars)
  if (limited.length > 0) {
    formatted = limited.substring(0, 2)
  }
  
  // Add remaining digits with specific spacing: DE XXXX XXX
  if (limited.length > 2) {
    const digits = limited.substring(2)
    formatted += ' '
    
    // First 4 digits
    for (let i = 0; i < Math.min(4, digits.length); i++) {
      formatted += digits[i]
    }
    
    // Space before next 3 digits
    if (digits.length > 4) {
      formatted += ' '
      for (let i = 4; i < digits.length; i++) {
        formatted += digits[i]
      }
    }
  }
  
  return formatted
}

/**
 * Handle input changes with auto-formatting and validation
 */
function handleInput(event, formData, updateField) {
  const input = event.target.value
  const cursorPosition = event.target.selectionStart
  
  // Format the input
  const formatted = formatPrefix(input)
  
  // Update field with formatted value
  updateField('prefix', formatted)
  
  // Clear error message
  errorMessage.value = ''
  
  // Calculate new cursor position (accounting for auto-inserted spaces)
  const oldLength = input.length
  const newLength = formatted.length
  const lengthDiff = newLength - oldLength
  
  // Set cursor position after Vue updates the DOM
  setTimeout(() => {
    if (event.target) {
      // If we added spaces, move cursor forward
      let newCursorPos = cursorPosition + lengthDiff
      
      // Don't place cursor right after auto-inserted spaces
      if (formatted[newCursorPos - 1] === ' ' && lengthDiff > 0) {
        newCursorPos++
      }
      
      event.target.setSelectionRange(newCursorPos, newCursorPos)
    }
  }, 0)
  
  // Validate format
  if (formatted && !isValidFormat(formatted)) {
    errorMessage.value = t('views.more.eartagPrefix.modal.error')
  }
}

/**
 * Show toast notification
 */
function showToastNotification(message, type = 'saved') {
  showToast.value = false
  toastMessage.value = message
  toastType.value = type
  setTimeout(() => {
    showToast.value = true
  }, 10)
}

/**
 * Save prefix
 */
function savePrefix(formData) {
  const prefix = formData.prefix.trim()
  uiStore.setEartagPrefix(prefix)
  showEditDialog.value = false
  
  // Show toast notification immediately in this view
  showToastNotification(t('views.more.eartagPrefix.modal.success'), 'saved')
}
</script>
